<template>
  <div class="tw-container min-h-screen">
    <SimplifiedHeroNew
      :title="onboardingTextConfig.knowledgeBaseSimplifiedTitle"
      :subtitle="onboardingTextConfig.knowledgeBaseSimplifiedSubtitle"
      :action-button="heroActionButton"
      :dropdown-actions="categoryDropdownActions"
    />

    <!-- Filters -->
    <KnowledgeBaseFilters
      :categories="categories"
      :community-id="communityId"
      @search-changed="handleSearchChanged"
      @category-changed="handleCategoryChanged"
      @bookmarked-filter-changed="handleBookmarkedFilterChanged"
      @filters-cleared="handleFiltersCleared"
    />

    <!-- Main Content -->
    <div class="pb-12 relative">
      <!-- Overview View -->
      <div v-if="currentView === 'overview'">
        <KnowledgeBaseContent
          :search-query="searchQuery"
          :category-filter="categoryFilter"
          :bookmarked-only="bookmarkedOnly"
          :community-id="communityId"
          :spotting-area-id="spottingAreaId"
          :is-edit-mode="isEditMode"
          @view-category="showCategoryGrid"
        />
      </div>

      <!-- Category Grid View -->
      <CategoryGridView
        v-else-if="currentView === 'category'"
        :category="selectedCategory"
        :categories="categories"
        :search-query="searchQuery"
        :community-id="communityId"
        :spotting-area-id="spottingAreaId"
        :bookmarked-only="bookmarkedOnly"
        :is-edit-mode="isEditMode"
        @go-back="goBackToOverview"
      />
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import UiMixin from '@/util/UiMixin'
  import TranslationsMixin from '@/util/TranslationsMixin'
  import Icon from '@/components/Icon/Icon.vue'
  import KnowledgeBaseContent from '@/Domain/KnowledgeBase/Components/KnowledgeBaseContent.vue'
  import CategoryGridView from '@/Domain/KnowledgeBase/Components/CategoryGridView.vue'
  import KnowledgeBaseFilters from '@/Domain/KnowledgeBase/Components/KnowledgeBaseFilters.vue'
  import { fetchCategories } from '@/api/knowledge-base'
  import DropdownActionsButton from '@/Domain/Shared/Components/DropdownActionsButton.vue'
  import { DropdownAction } from '@/Domain/Shared/shared-types'
  import { MUTATION_TYPES } from '@/store/modules/ui'
  import MODAL_IDS from '@/constants/modal-ids'
  import { fetchKnowledgeBaseCategories } from '@/Domain/KnowledgeBase/Api/knowledgeBase'
  import OnboardingMixin from '@/util/OnboardingMixin'
  import SimplifiedHeroNew from '@/components/SimplifiedHero/SimplifiedHeroNew.vue'

  export default defineComponent({
    name: 'KnowledgeBase',
    components: {
      SimplifiedHeroNew,
      DropdownActionsButton,
      CategoryGridView,
      KnowledgeBaseContent,
      KnowledgeBaseFilters,
      Icon
    },
    mixins: [UiMixin, TranslationsMixin, OnboardingMixin],
    data() {
      return {
        currentView: 'overview', // 'overview' or 'category'
        selectedCategory: null,
        categories: [],
        searchQuery: '',
        categoryFilter: null,
        bookmarkedOnly: false,
        realCategories: [], // Categories loaded from API
        customCategoryOrder: [],
        useCustomOrder: false,
        isLoadingCategories: false,
        isEditMode: false, // Edit mode for drag and drop
      }
    },
    computed: {
      canCreateKnowledgeBasePost() {
        // If we're in the spotting area context, check if the:
        // - user is a contributor to the spotting area
        // - or team member / owner
        // then it can create a KB post
        // The check "canUserUploadContent" only checks the general permissions which apply for all roles, except portfolio member
        // TODO: check what an ecosystem member can do in terms of linking spotting areas
        if (this.$store.getters.isMember) {
          return true
        }

        // If we're not in a spotting area context, use the general permission
        if (!this.spottingAreaId) {
          return this.$store.getters.canUserUploadContent
        }

        // If we are in a spotting area context and the user is a portfolio member, check if they contribute to this spotting area
        if (this.$store.getters.isPortfolioMember) {
          return this.$store.getters.contributeSpottingAreaIds.includes(Number(this.spottingAreaId))
        }

        return false
      },
      spottingAreaId() : Number | null {
        if (this.$route.params.spottingAreaId) {
          return Number(this.$route.params.spottingAreaId)
        }

        return null
      },
      communityId(): Number | null {
        if (this.$route.params.communityId) {
          return Number(this.$route.params.communityId)
        }

        return null
      },
      heroActionButton() {
        return this.canCreateKnowledgeBasePost && {
          label: this.$t('knowledge_base_add_post'),
          icon: 'plus',
          variant: 'rounded',
          onClick: () => this.navigateToCreate()
        }
      },
      categoryDropdownActions(): DropdownAction[] {
        return [
          {
            onClick: () => {
              this.openAddCategoryModal()
            },
            label: this.$t('knowledge_base_add_category'),
            icon: 'plus',
            analyticsId: 'knowledgeBase.addCategory'
          },
          {
            onClick: () => {
              this.openReorderCategoriesModal()
            },
            label: this.$t('knowledge_base_reorder_categories'),
            icon: 'sort',
            analyticsId: 'knowledgeBase.reorderCategory'
          },
          (this.isOwner || this.isMember) && {
            onClick: () => {
              this.toggleEditMode()
            },
            label: this.isEditMode ? this.$t('knowledge_base_exit_edit_mode') : this.$t('knowledge_base_enter_edit_mode'),
            icon: this.isEditMode ? 'x' : 'edit',
            analyticsId: 'knowledgeBase.toggleEditMode'
          },
        ].filter(Boolean)
      },
    },
    async mounted() {
      // Load categories for the grid view
      await this.loadRealCategories()
      await this.loadCategories()
    },
    methods: {
      openAddCategoryModal() {
        this.$store.commit(MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.ADD_KNOWLEDGE_BASE_CATEGORY)
      },
      openReorderCategoriesModal() {
        // Pass current categories to the modal for reordering
        // Modal will save changes to database when user confirms
        const modalContext = {
          categories: this.categories.map(cat => ({ id: cat.id, name: cat.name })),
          currentOrder: this.customCategoryOrder,
          useCustomOrder: this.useCustomOrder
        }
        this.$store.commit(MUTATION_TYPES.SET_MODAL_CONTEXT, modalContext)
        this.$store.commit(MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.REORDER_CATEGORIES)
      },
      async loadRealCategories() {
        this.isLoadingCategories = true
        try {
          await fetchKnowledgeBaseCategories()
            .then((response) => {
              // Categories are already ordered by the backend (by 'order' field, then by 'name')
              this.realCategories = response.data

              // Set up the category order based on the backend ordering
              if (this.realCategories && this.realCategories.length > 0) {
                // Categories are already ordered by the backend, so preserve that order
                this.customCategoryOrder = this.realCategories.map(cat => cat.id)
                this.useCustomOrder = true
              }
            })

        } catch (error) {
          console.error('Failed to load categories:', error)
        } finally {
          this.isLoadingCategories = false
        }
      },
      async loadCategories() {
        try {
          const response = await fetchCategories()
          this.categories = response.data
        } catch (error) {
          console.error('Failed to load categories:', error)
        }
      },

      navigateToCreate() {
        // Navigate to knowledge base create page
        if (this.$route.params.communityId) {
          this.$router.push(`/communities/${this.$route.params.communityId}/knowledge-base/create`)
        } else if (this.$route.params.spottingAreaId) {
          this.$router.push(`/spotting-areas/${this.$route.params.spottingAreaId}/knowledge-base/create`)
        } else {
          this.$router.push('/knowledge-base/create')
        }
      },

      showCategoryGrid(category) {
        this.selectedCategory = category
        this.currentView = 'category'
      },

      goBackToOverview() {
        this.currentView = 'overview'
        this.selectedCategory = null

        this.$nextTick(() => {
          // Clear the category filter when going back to overview
          this.categoryFilter = null
        })
      },

      handleSearchChanged(searchQuery) {
        this.searchQuery = searchQuery
      },

      handleCategoryChanged(categoryId) {
        // Instead of filtering, open the grid view for the selected category
        if (categoryId === null) {
          // If "All Categories" is selected, go back to overview
          this.goBackToOverview()
          return
        }

        if (categoryId === 'uncategorized') {
          // Handle uncategorized posts - pass null to showCategoryGrid
          this.showCategoryGrid(null)
          return
        }

        // Find the category object by ID
        const category = this.categories.find(cat => cat.id === categoryId)
        if (category) {
          this.showCategoryGrid(category)
        }
      },

      handleBookmarkedFilterChanged(showBookmarkedOnly) {
        this.bookmarkedOnly = showBookmarkedOnly
      },

      handleFiltersCleared() {
        this.searchQuery = ''
        this.categoryFilter = null
        this.bookmarkedOnly = false
      },

      toggleEditMode() {
        this.isEditMode = !this.isEditMode
      },

    }
  })
</script>
